import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDataAuth } from "../../../hooks/useDataAuth";
import { callApi } from "../../../services/api.service";
import { useNotification } from '../../../hooks/useNotification';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { useForm } from '../../../hooks/useForm';
import { useLocation } from 'react-router-dom';
import { CHILD_TYPE_ACTIONS, EMPTY_ACTION, INITIAL_INTERFACE_FORM, MENU_TYPE_ACTIONS } from '../constants/interface.constants';
import { buildInterfacePayload, normalizeInterfaceForForm } from '../helpers/helpers';

export const useManageInterface = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [{ userSession }] = useDataAuth();
    const [sendNotification] = useNotification();
    const [isEdit, setisEdit] = useState(false);
    const [workFlowLists, setWorkFlowLists] = useState([]);

    const [errorInputs, , resetErrors, validForm] = useErrorInputs({});
    const [values, handleInputChange, handleDirectInputChange, setAllInfo, , resetExcept] = useForm(INITIAL_INTERFACE_FORM);

    useEffect(() => {
        if (!isEdit) {
            resetExcept(['type_action']);
            resetErrors();
            if (CHILD_TYPE_ACTIONS.includes(values.type_action)) {
                handleDirectInputChange('configuration', { ...EMPTY_ACTION });
                handleDirectInputChange('flow_process', []);
            } else if (MENU_TYPE_ACTIONS.includes(values.type_action)) {
                handleDirectInputChange('configuration', null);
                handleDirectInputChange('flow_process', []);
            } else {
                handleDirectInputChange('configuration', null);
                handleDirectInputChange('flow_process', []);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.type_action])

    const validateForm = () => {
        const requiredFields = ['type_action', 'name', 'icon'];
        if (values.type_action === 'MENU') {
            requiredFields.push('work_flow', 'path', 'menu', 'order_menu', 'name_action');
        } else if (values.type_action === 'SUBMENU') {
            requiredFields.push('work_flow', 'order_menu', 'name_action');
        } else if (values.type_action === 'CHART_REPORT') {
            requiredFields.push('work_flow', 'path', 'menu', 'order_menu');
        } else if (CHILD_TYPE_ACTIONS.includes(values.type_action)) {
            requiredFields.push('work_flow_related', 'order_menu');
        } else {
            requiredFields.push('work_flow');
        }
        return validForm(requiredFields, values);
    };

    const getActions = () => {
        callApi('user_action_select', 'SELECT_WORK_FLOWS', {}, userSession.token)
            .then(({ data }) => {
                setWorkFlowLists(data);
            }).catch((e) => {
                console.error("ERROR_getActions", e);
            })
    }

    const getInterfaceById = () => {
        if (location.state && location.state.config) {
            const { config } = location.state;
            if (config.data?.id) {
                setisEdit(true);
                const id = config.data.id;
                callApi('user_action_select', 'SELECT_BY_ID', { id_user_action: id }, userSession.token, { findOne: "true" })
                    .then(({ data }) => {
                        if (typeof data.configuration === 'string' && data.configuration.includes('charts'))
                            data.type_action = 'CHART_REPORT';
                        setAllInfo(normalizeInterfaceForForm(data));
                    }).catch((e) => {
                        console.error("ERROR_getGroupById", e);
                    })
            }
        }
    };

    useEffect(() => {
        const init = async () => {
            await Promise.all([
                getInterfaceById(),
                getActions(),
            ]);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = (data) => {
        const payload = buildInterfacePayload(data);
        callApi('user_action_alter', isEdit ? 'UPDATE' : 'INSERT', payload, userSession.token)
            .then(() => {
                sendNotification({ variant: 'success', msg: t("messages.saveSuccess") });
            }).catch((e) => {
                sendNotification({ variant: 'error', msg: t("messages.saveError") });
            })
    };

    return {
        values,
        errorInputs,
        workFlowLists,
        isEdit,
        handleInputChange,
        handleDirectInputChange,
        validateForm,
        save,
        t
    };
}
